import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle2, X, Camera, Loader2, Trash2 } from "lucide-react";
import { compressImage } from "@/lib/imageCompress";
import { completeRecommendation } from "@/lib/api";
import { toast } from "sonner";

export default function HandleFlow({ open, onOpenChange, recommendation, onCompleted }) {
  const [notes, setNotes] = useState("");
  const [attachments, setAttachments] = useState([]); // { fileName, mimeType, sizeBytes, data }
  const [busy, setBusy] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const fileInput = useRef(null);

  if (!recommendation) return null;

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCompressing(true);
    try {
      const att = await compressImage(f);
      setAttachments((prev) => [...prev, att]);
    } catch (err) {
      toast.error("Couldn't process that image.", { description: err.message });
    } finally {
      setCompressing(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const removeAttachment = (i) =>
    setAttachments((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    setBusy(true);
    try {
      await completeRecommendation(recommendation.id, {
        notes: notes.trim() || undefined,
        attachments,
        completedAt: new Date().toISOString(),
      });
      toast.success("Marked complete", { description: "Nice work — one less thing to think about." });
      onCompleted?.();
      onOpenChange(false);
      setNotes("");
      setAttachments([]);
    } catch (err) {
      toast.error("Something went wrong", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg p-0 gap-0 rounded-[24px] overflow-hidden max-h-[90vh] overflow-y-auto"
        data-testid="handle-flow-modal"
      >
        <DialogHeader className="px-6 pt-6">
          <div className="flex items-start gap-3">
            <div
              className="w-11 h-11 rounded-xl inline-flex items-center justify-center shrink-0"
              style={{ background: "var(--ta-success-bg)", color: "var(--ta-success)" }}
            >
              <CheckCircle2 size={22} strokeWidth={2.2} />
            </div>
            <div className="flex-1">
              <DialogTitle className="font-display text-2xl font-semibold" style={{ color: "var(--ta-primary)" }}>
                Mark this complete
              </DialogTitle>
              <DialogDescription className="text-sm mt-1" style={{ color: "var(--ta-text-secondary)" }}>
                {recommendation.title}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="px-6 pt-4 pb-6 flex flex-col gap-5">
          <div>
            <label className="text-sm font-medium mb-2 inline-block" style={{ color: "var(--ta-primary)" }}>
              Any notes for future you? <span style={{ color: "var(--ta-text-secondary)" }}>(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="handle-notes-input"
              placeholder="e.g. Updated summer hours to 7am–7pm on Google. Took 2 minutes."
              rows={4}
              className="w-full rounded-2xl bg-white border-2 p-4 text-base outline-none focus:border-[var(--ta-primary)] resize-none"
              style={{ borderColor: "var(--ta-border)", color: "var(--ta-text)" }}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 inline-block" style={{ color: "var(--ta-primary)" }}>
              Proof photo? <span style={{ color: "var(--ta-text-secondary)" }}>(optional)</span>
            </label>
            {attachments.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-2" data-testid="handle-attachments">
                {attachments.map((a, i) => (
                  <div
                    key={i}
                    className="relative aspect-square rounded-xl overflow-hidden border"
                    style={{ borderColor: "var(--ta-border)" }}
                  >
                    <img src={a.data} alt={a.fileName} className="w-full h-full object-cover" />
                    <button
                      onClick={() => removeAttachment(i)}
                      data-testid={`handle-remove-attachment-${i}`}
                      className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-white inline-flex items-center justify-center"
                      aria-label="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              onChange={handleFile}
              className="hidden"
              data-testid="handle-file-input"
            />
            <button
              onClick={() => fileInput.current?.click()}
              disabled={compressing}
              data-testid="handle-pick-photo"
              className="w-full inline-flex items-center justify-center gap-2 min-h-[56px] rounded-2xl text-base font-medium border-2 border-dashed disabled:opacity-60"
              style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)", background: "var(--ta-surface-alt)" }}
            >
              {compressing ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Compressing…
                </>
              ) : (
                <>
                  <Camera size={18} /> {attachments.length ? "Add another" : "Add a photo"}
                </>
              )}
            </button>
            <p className="mt-2 text-xs" style={{ color: "var(--ta-text-secondary)" }}>
              We'll shrink it before saving — no need to worry about size.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <button
              onClick={() => onOpenChange(false)}
              data-testid="handle-cancel"
              className="flex-1 min-h-[56px] rounded-2xl text-base font-medium border-2"
              style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)", background: "#fff" }}
            >
              Not yet
            </button>
            <button
              onClick={submit}
              disabled={busy}
              data-testid="handle-submit"
              className="flex-1 min-h-[56px] rounded-2xl text-base font-medium text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              style={{ background: "var(--ta-primary)" }}
            >
              {busy ? (
                <span className="inline-flex items-center gap-2 justify-center"><Loader2 size={18} className="animate-spin" /> Saving…</span>
              ) : (
                "Mark complete"
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
